<template>
  <tr class="text-sm font-semibold">
    <!-- NOME -->
    <td>
      <div class="row relative h-full font-bold text-gray-50">
        <img
          :src="`/img/${data.best_symbol.toLowerCase()}.jpg`"
          class="absolute top-1/2 left-0 h-full w-full -translate-y-1/2 transform object-cover opacity-50"
        />
        <div class="to-ontick-graphite-500 absolute inset-0 bg-gradient-to-r from-transparent"></div>
        <div class="relative z-10 flex flex-col">
          <span class="text-shadow text-base font-bold text-gray-50">
            {{ data.label }}
          </span>
          <template v-if="symbolInfo">
            <span class="text-xs font-semibold text-gray-300 drop-shadow-md" v-if="getSymbolPrice(data.best_symbol)">
              {{ getSymbolPrice(data.best_symbol).toFixed(symbolInfo.decimal_places) }}
            </span>
            <span class="w-full text-xs font-semibold text-gray-300 drop-shadow-md" v-else>
              {{ symbolInfo.last_price.toFixed(symbolInfo.decimal_places) }}
            </span>
          </template>
          <span class="text-xs font-semibold text-gray-50">
            {{ data.best_symbol }}
          </span>
        </div>
      </div>
    </td>

    <!-- TENDÊNCIA/TARGET/ENCERRAMENTO -->
    <td>
      <BestSvgSparklineComponent v-if="svg" :symbol="data.best_symbol" @click="openTrendModal" />
      <BestSparklineComponent v-else :symbol="data.best_symbol" @click="openTrendModal" />
    </td>

    <!-- MULTIPLICADOR/VOLUME -->
    <td>
      <div class="row flex items-center gap-2">
        <button
          v-for="multiplier in multipliers"
          :key="multiplier"
          class="btn-shadow rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-100 hover:brightness-125"
          :class="multiplier == selected_multiplier ? 'bg-ontick-blue-900' : 'bg-ontick-steel-600'"
          @click="selected_multiplier = multiplier"
        >
          {{ multiplier }}x
        </button>
      </div>
    </td>

    <!-- OBJETIVO -->
    <td>
      <div class="row flex items-center gap-2">
        <button
          v-for="target in targets"
          :key="target"
          class="rounded-lg px-3 py-2 text-sm font-semibold transition-all duration-100 hover:brightness-125 disabled:pointer-events-none disabled:text-gray-500"
          :class="[
            target == selected_target ? 'bg-ontick-blue-900' : 'bg-ontick-steel-600',
            { 'btn-shadow': !blockedTargets.includes(target) },
          ]"
          :disabled="blockedTargets.includes(target)"
          @click="selected_target = target"
        >
          {{ target }}{{ targetType }}
        </button>
      </div>
    </td>

    <!-- DIREÇÃO/PARAR -->
    <td>
      <div class="row grid grid-cols-2 gap-2">
        <div class="col-span-1 w-full">
          <button
            v-tippy="{ arrow: true, theme: 'green' }"
            content="Sobe"
            class="border-ontick-green-600 call-btn group"
            :disabled="buttonIsDisabled"
            @click="bestCall('buy')"
          >
            <div class="bg-ontick-green-600 call-btn-icon">
              <i v-html="$icons.best_call_long"></i>
            </div>
            <div class="flex w-[100px] items-center justify-start px-3 py-2 font-bold">
              <span v-if="buttonIsDisabled">-</span>
              <span v-else> R${{ pnl }} </span>
            </div>
          </button>
        </div>
        <div class="col-span-1 w-full">
          <button
            v-tippy="{ arrow: true, theme: 'orange' }"
            content="Desce"
            class="border-ontick-orange-600 call-btn group"
            :disabled="buttonIsDisabled"
            @click="bestCall('sell')"
          >
            <div class="bg-ontick-orange-600 call-btn-icon">
              <i v-html="$icons.best_call_short"></i>
            </div>
            <div class="flex w-[100px] items-center justify-start px-3 py-2 font-bold">
              <span v-if="buttonIsDisabled">-</span>
              <span v-else> R${{ pnl }} </span>
            </div>
          </button>
        </div>
      </div>
    </td>
  </tr>
</template>

<script>
import { mapGetters } from "vuex";
import { iconsSet as icons } from "../../../assets/icons/icons";
import event_bus from "../../../shared/utils/event_bus";
import symbol_images from "../symbol_images";
import BestSparklineComponent from "./best-sparkline.component.vue";
import BestSvgSparklineComponent from "./best-svg-sparkline.component.vue";
export default {
  mixins: [symbol_images],

  components: {
    BestSparklineComponent,
    BestSvgSparklineComponent,
  },

  props: {
    data: {
      type: Object,
    },
    selected_tab: {
      type: String,
    },
  },

  data() {
    return {
      selected_multiplier: null,
      selected_target: null,
      svg: true,
    };
  },

  methods: {
    bestCall(type) {
      event_bus.$emit("BEST_CALL", {
        type,
        best_data: this.data,
        selected_multiplier: this.selected_multiplier,
        selected_target: this.selected_target,
        pnl: this.pnl,
      });
    },
    openTrendModal() {
      event_bus.$emit("OPEN_TREND_MODAL");
    },
  },

  computed: {
    ...mapGetters({
      getSymbolData: "bests/getSymbolData",
      getSymbolPrice: "marketdata/getSymbolPrice",
      getUserBests: "bests/getUserBests",
    }),
    trendInfo() {
      const trend = this.getSymbolData.find((item) => item.symbol == this.data.best_symbol).symbol_trend;
      switch (trend) {
        case "UP":
          return {
            trend,
            icon: icons.trend_up,
            tooltip: "Tendência de alta",
          };
        case "DOWN":
          return {
            trend,
            icon: icons.trend_down,
            tooltip: "Tendência de baixa",
          };
        default:
          return {
            trend,
            icon: icons.trend_sideways,
            tooltip: "Tendência lateral",
          };
      }
    },
    targetType() {
      switch (this.data.best_target_type) {
        case "POINTS":
          return "p";
        default:
          return "%";
      }
    },
    multipliers() {
      return this.data.best_multipliers.split(",").map((item) => Number(item));
    },
    targets() {
      return this.data.best_targets.split(",").map((item) => Number(item));
    },
    blockedTargets() {
      const same_symbol_running_bests = this.getUserBests.filter((item) => item.bo_symbol == this.data.best_symbol);
      if (same_symbol_running_bests.length > 0) {
        return same_symbol_running_bests.map((item) => item.bo_target);
      }
      return [];
    },
    buttonIsDisabled() {
      return this.blockedTargets.length == this.targets.length;
    },
    symbolInfo() {
      return this.getSymbolData.find((item) => item.symbol == this.data.best_symbol);
    },
    pnl() {
      const symbol = this.data.best_symbol;
      const target = Number(this.selected_target);
      const multiplier = Number(this.selected_multiplier);
      const symbol_data = this.getSymbolData.find((item) => item.symbol == symbol);
      const { min_volume, tick_size, tick_profit, symbol_market } = symbol_data;
      const last_price = this.getSymbolPrice(symbol) || symbol_data.last_price;

      if (symbol == "BITFUT") {
        return (((last_price * (target / 100)) / tick_size) * tick_profit * multiplier).toFixed(2);
      } else if (symbol_market == "FUTURES") {
        return ((target / tick_size) * tick_profit * multiplier).toFixed(2);
      }
      return (last_price * (target / 100) * min_volume * multiplier).toFixed(2);
    },
  },

  mounted() {
    this.selected_multiplier = this.multipliers[0];
    const selected_target_index = this.targets.findIndex((target) => !this.blockedTargets.includes(target));
    if (selected_target_index != -1) {
      this.selected_target = this.targets[selected_target_index];
    }
  },
};
</script>

<style lang="scss" scoped>
@use "../styles/bests.scss";
</style>
